import { Button, Collapse, Empty, InputNumber, message, Popover, Spin } from 'antd'
import DoubleDown from '@/assets/DoubleDown.svg'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
  getCalcQuoteBudgetPrice,
  quoteBudgetPriceQueryTree,
  refreshQuoteBudgetPrice,
} from '@/services/order.ts'
import { isArray } from 'lodash-es'
import { useParams } from 'react-router-dom'

const { Panel } = Collapse
import styles from './index.module.less'
import { numFormat } from 'shj-components/dist/es/SFilterTable'

const PriceDetailPopover = forwardRef((props: any, ref) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [treeData, setTreeData] = useState<any>([])
  const [open, setOpen] = useState(false)
  const [budgetId, setBudgetId] = useState(useParams()?.budgetId || '')
  const [couldOperate, setCouldOperate] = useState(true)
  const timeout = useRef<any>(null)
  const { mode, couldEdit } = props
  // 1. 初始化
  useEffect(() => {
    if (open) {
      init()
    }
  }, [open])
  useEffect(() => {
    setCouldOperate(couldEdit)
  }, [couldEdit])
  useImperativeHandle(ref, () => ({
    reset: id => {
      if (id) setBudgetId(id)
      init(id)
    },
    setOperate: flag => {
      setCouldOperate(flag)
    },
  }))
  // 根据id,更新价格
  const updateTreeById = (tree, id, newData) => {
    return tree.map(node => {
      if (node.id === id) {
        return { ...node, object: { ...node.object, ...newData } }
      }
      if (node.children) {
        const updatedChildren = updateTreeById(node.children, id, newData)
        return { ...node, children: updatedChildren }
      }
      return node
    })
  }
  // 验证
  const isEmpty = value => {
    return value === null || value === undefined || value === ''
  }
  const validateTree = (tree, validateFn) => {
    console.log('验证tree', tree)

    for (const node of tree) {
      if (validateFn(node)) {
        return {
          label: node?.label,
          status: false,
        } // 如果验证失败，返回false
      }
      if (node.children && node.children.length > 0) {
        const result = validateTree(node.children, validateFn).status
        if (!result) {
          return {
            label: node?.label,
            status: false,
          } // 如果子节点验证失败，返回false
        }
      }
    }
    return {
      label: '',
      status: true,
    } // 所有节点验证通过，返回true
  }
  // 查询价格
  const debouncedSetValue = (treeEntityQuoteBudgetPriceTreeLongs, item) => {
    setLoading(true)
    getCalcQuoteBudgetPrice({
      params: {
        quoteBudgetId: budgetId,
      },
      data: treeEntityQuoteBudgetPriceTreeLongs,
    })
        .then(res => {
          setTreeData(res || [])
        })
        .finally(() => {
          setLoading(false)
        })
  }
  // 改变价格
  const changePrice = (newValue, item)=>{
    const valueInput = limitDecimals(newValue);
    const treeEntityQuoteBudgetPriceTreeLongs = updateTreeById(treeData, item.id, {
      price: valueInput || "",
      editedFlag: true,
    })
    setTreeData(treeEntityQuoteBudgetPriceTreeLongs || [])
    if (timeout?.current) {
      clearTimeout(timeout?.current);
    }
    timeout.current = setTimeout(() => {
      debouncedSetValue(treeEntityQuoteBudgetPriceTreeLongs, item)
    }, 1500)
  }

  const init = (id?: string) => {
    setLoading(true)
    quoteBudgetPriceQueryTree({
      params: {
        quoteBudgetId: id || budgetId,
      },
    })
      .then((res: any) => {
        if (isArray(res)) {
          setTreeData([...res])
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }
  // 验证是否可编辑
  const validateShowBtnFn = node => (node?.object?.mathType === 'CREATOR_ENTRY' || node?.object?.editFlag) && couldOperate;
  // 验证未填写
  const validateFn = node => validateShowBtnFn(node) && isEmpty(node?.object?.price);
  // 点击保存
  const saveFn = () => {
    if (!validateTree(treeData, validateFn).status) {
      return message.error(`请填写${validateTree(treeData, validateFn)?.label}！`)
    }
    setLoading(true)
    refreshQuoteBudgetPrice({
      params: {
        quoteBudgetId: budgetId,
      },
      data: treeData,
    })
      .then(() => {
        init()
        setOpen(false)
        props?.onCancel && props?.onCancel()
        props?.reloadList && props?.reloadList()
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
  }
  const limitDecimals = (value) => {
    const reg = /^(\-)*(\d+)\.(\d\d).*$/;
    return String(value).replace(reg, '$1$2.$3')
  };
  // 编辑框
  const renderEditInput = it => {
    if (validateShowBtnFn(it)) {
      return (
        <InputNumber
          step="0.01"
          type="number"
          formatter={limitDecimals}
          parser={limitDecimals}
          onInput={e => {
            changePrice(e, it)
          }}
          size="small"
          value={it?.object?.price}
          prefix="￥"
          style={{ width: 120 }}
          onClick={e => {
            e.stopPropagation()
          }}
        />
      )
    }
    return (
      <div
        className={`${styles.price} ${['TOTAL_PROJECT_COST', 'CONTRACT_AMOUNT', 'DISCOUNT_SUMMARY', 'OTHER_PAYMENT_ITEMS'].includes(it?.object?.type) ? styles.redPrice : ''}`}
      >
        ￥{it?.object?.price ? `${numFormat({ value: it?.object?.price })}` : '0.00'}
      </div>
    )
  }
  // 渲染列表
  const renderItem = item => {
    return (
      <div className={styles.header}>
        <Popover content={item?.object?.name}>
          <div className={styles.label}>{item?.object?.name}</div>
        </Popover>
        {renderEditInput(item)}
      </div>
    )
  }

  const renderTreeComponent = (treeData, level) => {
    return (
      <div>
        {(treeData || []).map((item, index) => {
          if ((item?.children || []).length === 0) {
            return (
              <>
                <div
                  className={`${styles.noSubHeader} ${level === 0 ? styles.levelFirst : ''}`}
                  key={item?.id}
                >
                  {renderItem(item)}
                </div>
              </>
            )
          }
          return (
            <Collapse ghost defaultActiveKey={['1']}>
              <Panel
                header={
                  <div className={`${level === 0 ? styles.levelFirst : ''}`}>
                    {renderItem(item)}
                  </div>
                }
                key={'1'}
              >
                {renderTreeComponent(item?.children, level + 1)}
              </Panel>
            </Collapse>
          )
        })}
      </div>
    )
  }

  const countContent = (
    <Spin spinning={loading}>
      {(treeData || []).length > 0 ? (
        <div className={styles.priceDetailPopover}>
          <div className={styles.priceList}>{renderTreeComponent(treeData, 0)}</div>
          {!validateTree(treeData, validateShowBtnFn).status ? (
            <div className={styles.btnBox}>
              <Button
                onClick={() => {
                  setOpen(false)
                  props?.onCancel()
                }}
              >
                取消
              </Button>
              <Button type="primary" onClick={saveFn}>
                保存
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.priceDetailPopover}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
    </Spin>
  )

  if (mode && mode === 'card' && props?.open) {
    return countContent
  }
  return (
    <Popover
      destroyTooltipOnHide
      onOpenChange={handleOpenChange}
      open={open}
      content={countContent}
      title={'价格明细'}
      trigger="click"
      style={{ width: 500 }}
    >
      <div className="flex-center">
        <img src={DoubleDown} alt="" />
        <span>金额明细</span>
      </div>
    </Popover>
  )
})
export default PriceDetailPopover
